// 2026-07-23 极简 composer: MailV2PageComposer — 单一组件持有全部 draft 状态。
//
// 设计原则:
//   1. **单一数据源**:useDraftForm 提供 { draft, isDirty, setField, setDraft, markSaved, resetToEmpty }
//   2. **单向数据流**:Editor 看到什么 = draft;改字段 = setField;无任何 fallback state
//   3. **显式 state machine**:8 个 useState 描述全部状态(见下方状态表)
//   4. **不调 API 的 hook**:useDraftForm 纯状态;API 调用在本组件内显式 await
//   5. **requestSeqRef 防止 stale response**:切 draft 时 +1,旧 GET response 拒绝回写
//   6. **保存只在 close/send/delete 触发**:无 autosave
//
// 状态表:
//   mode:             "new" | "existing"
//   selectedDraftId:  string | null   (与 mode 强相关)
//   draft:            MailDraftForm  (Editor 看到的对象;null 表示 loading)
//   loading:          boolean        (GET /drafts/{id} 飞行中)
//   saving:           boolean        (POST /drafts 飞行中)
//   sending:          boolean        (POST /drafts/{id}/send 飞行中)
//   deleting:         boolean        (DELETE /drafts/{id} 飞行中)
//   saveStatus:       SaveStatus     (idle/dirty/saving/saved/error/loading)
//   error:            string | null
//
// 4 个 flow:
//   open:    handleNew() → resetToEmpty;handleOpenDraft(id) → GET id → setDraft
//   close:   handleUserClose() → if dirty upsert;else just close
//   send:    handleSend() → upsert(if dirty) → POST /send → onSent → close
//   delete:  handleDelete() → DELETE id → onDeleted → close
//
// 不做的事:
//   - 不做 autosave
//   - 不在 hook 内部发起 API
//   - 不在 mount 时自动 load 任何东西(由父组件 prop existingDraftId 决定)
//   - 不在 close 时不调 setDraft 就不卸载(dialog 父级 {open && <Component key={sessionKey} />} 控制)

import { useCallback, useEffect, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import { X, AlertCircle } from "lucide-react";
import { mailV2Api, type MailAttachmentDTO } from "../../../api/mail-v2";
import {
  useDraftForm,
  createEmptyDraftForm,
  type MailDraftForm,
} from "../composer/useDraftForm";
import { MailEditor } from "../composer/MailEditor";
import type { SaveStatus } from "../composer/mailComposerTypes";
import { useAttachments } from "../hooks/useAttachments";
import { plainTextToHtml } from "../composer/plainTextHtml";

// 2026-07-23: Composer 内部自己持 useAttachments 实例 — 它绑定到当前 draft.id,
// 这样无论 dialog 内的 draft 是新建 (id 还没有 → 不发请求) 还是编辑已有 draft,
// 附件都跟当前 draft 强绑定,父组件不需要操心 draftId 同步。
// demo mode 时(MAIL_V2_REAL=false)整个 hook no-op,items 永远 [].

export interface MailV2PageComposerProps {
  open: boolean;
  /** 父组件提供的 sessionKey;同时作为 React key,让 close → reopen 时旧 state 销毁 */
  sessionKey: string;
  /** 已存在 draft id(null = 新建邮件) */
  existingDraftId: string | null;
  /** mail account id */
  mailAccountId: string;
  /** 父级 message id */
  parentMessageId?: string | null;
  /** Reply / Reply-All / Forward の初期値。existingDraftId がある場合は draft API を優先。 */
  initialDraft?: Partial<MailDraftForm> | null;
  /** Reply / Forward 時だけ左側に出す元メール。新規作成では null。 */
  contextPane?: ReactNode;
  /** 关闭回调(已 await save 完成后才调) */
  onClose: () => void;
  /** 发送成功回调 */
  onSent: (sent: { id: string; subject: string | null }) => void;
  /** 删除成功回调 */
  onDeleted?: (deletedId: string) => void;
  /**
   * 真实 upsertDraft 成功回调(新建或更新)。
   *
   * 触发条件(全部满足才调):
   *   - isEmpty=false(空白 draft 不发 API → 不调)
   *   - 之前 isDirty=true(未修改的现有 draft 关闭时也走 performSave 但因 isDirty=false → 不发 API → 不调)
   *   - upsertDraft API 成功
   *
   * 不触发场景:
   *   - 空白新邮件直接关闭
   *   - 未修改的已有 Draft 关闭
   *   - 保存失败
   *
   * 父组件用此回调触发 refreshMailState()(folder counts + 当前 list)。
   * 不必等待 refresh 完成再关闭 — 刷新是后台 setReload 触发 useEffect。
   */
  onSaved?: (result: { draftId: string; isNew: boolean }) => void;
  /** 错误提示(父组件 toast) */
  onError: (msg: string) => void;

  /**
   * 父组件挂的 imperative handle — 用于外部触发「保存当前 draft」但不关闭。
   * 父组件在切 draft / 切 folder 前 await 此 handle:
   *   - dirty 时 await performSave()
   *   - not dirty 时直接 resolve
   *   - 已 saving 中 → 直接 resolve
   */
  requestCloseHandle?: MutableRefObject<(() => Promise<void>) | null>;
  /**
   * 2026-07-24: AI 生成结果插入 handle — 父组件 (MailV2Page) 触发。
   * 注册的函数 (text) => {form.setField("bodyText", ...); form.setField("bodyHtml", ...)}。
   * 模式与 requestCloseHandle 一致: imperative ref,父组件持 ref,Composer 在 useEffect 注册/清理。
   */
  insertBodyHandle?: MutableRefObject<((text: string) => void) | null>;
  /**
   * 2026-07-24: Compose 内部 AI 按钮入口 → 父组件打开 Drawer (mode=compose)。
   * 复用 MailComposerToolbar 已有的「AI 生成」按钮(原 className/icon/位置不变),
   * 由 MailV2Page → Composer → MailEditor → MailComposerToolbar 透传。
   */
  onOpenAiDrawer?: () => void;
  /**
   * 2026-07-24: 同一个 MailV2AiDrawer 在 compose 模式下挂在 dialog 内右侧。
   * MailV2Page 创建 JSX 后通过 ReactNode prop 传入,Composer 不感知 drawer 内部状态。
   * isAiDrawerOpen=true 时才渲染 .mv2-compose-ai-slot 容器。
   */
  aiDrawer?: ReactNode;
  isAiDrawerOpen?: boolean;
}

const UUIDV4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // 兜底:32 字符 hex
  return Array.from({ length: 4 }, () =>
    Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0"),
  ).join("");
}

const COMPOSER_REAL = import.meta.env.VITE_MAIL_V2_REAL === "true";

export function MailV2PageComposer({
  open,
  sessionKey,
  existingDraftId,
  mailAccountId,
  parentMessageId = null,
  initialDraft = null,
  contextPane = null,
  onClose,
  onSent,
  onDeleted,
  onSaved,
  onError,
  requestCloseHandle,
  insertBodyHandle,
  onOpenAiDrawer,
  aiDrawer,
  isAiDrawerOpen,
}: MailV2PageComposerProps) {
  // ---- 0. 状态(按 useState → useRef → useEffect → useCallback 顺序)----

  // ---- 0a. 8 个独立 useState 描述流程状态 ----
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  // ---- 0b. refs(在所有 useCallback 之前声明)----
  /** 切 draft 时 +1;旧 GET response 拒绝回写 */
  const requestSeqRef = useRef(0);
  /** 父级 onClose ref 跟随(避免 stale closure) */
  const onCloseRef = useRef<() => void>(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // ---- 1. 核心 draft state(单一 owner)----
  const form = useDraftForm(
    createEmptyDraftForm(mailAccountId) as MailDraftForm,
  );
  /** form ref 跟随 — 给 performSave / handleAttachFiles / handleDelete 拿 fresh form.draft */
  const formRef = useRef<typeof form>(form);
  formRef.current = form;
  /** onSaved ref 跟随 — 让 performSave 拿到 fresh callback(避免 stale closure) */
  const onSavedRef = useRef<typeof onSaved>(onSaved);
  onSavedRef.current = onSaved;

  // ---- 1b. 附件 hook(绑定到当前 draft.id;id 为 null 时不发请求)----
  // Composer 自管附件 — 父组件不需要知道 draftId 同步。
  // 注:form.draft.id 在每次 render 都是新字符串(从 useState 派生);
  // 这里直接传当前值,hook 内部 useEffect 监听变化。
  const attachments = useAttachments({
    draftId: form.draft.id,
    enabled: COMPOSER_REAL,
  });

  // ---- 2. performSave:upsert(POST /drafts)----
  // 内部使用:用户按 send / 切 draft 前 / 关闭 dialog / 上传附件(需要先有 draftId)
  //
  // 返回: { ok, draftId?, error?, isNew? }
  //   isNew=true 表示本次是 INSERT(之前 d.id===null);isNew=false 表示 UPDATE。
  //   isNew 只在真实 upsertDraft 成功后才设置(空 draft 跳过时 isNew 不设)。
  const performSave = useCallback(async (): Promise<{ ok: boolean; draftId?: string; error?: string; isNew?: boolean }> => {
    const d = formRef.current.draft;
    const isNew = !d.id; // 之前无 id → 本次是 INSERT
    // 空 draft 保护:全部字段都为空 → 不调 save,直接 ok(空 draft 不需要刷新)
    const isEmpty =
      !d.to.trim() &&
      !d.cc.trim() &&
      !d.subject.trim() &&
      !d.bodyText.trim() &&
      !d.bodyHtml.trim();
    if (isEmpty) {
      return { ok: true, draftId: d.id ?? undefined };
    }
    if (!d.mailAccountId) {
      return { ok: false, error: "メールアカウントが選択されていません" };
    }
    setSaving(true);
    setSaveStatus("saving");
    setError(null);
    try {
      const saved = await mailV2Api.upsertDraft({
        id: d.id ?? undefined,
        idempotency_key: d.id ? undefined : newIdempotencyKey(),
        mail_account_id: d.mailAccountId,
        parent_message_id: d.parentMessageId,
        to_addresses: d.to || undefined,
        cc_addresses: d.cc || undefined,
        subject: d.subject || undefined,
        body_text: d.bodyText || undefined,
        body_html: d.bodyHtml || undefined,
      });
      // 把后端返回的 id 同步回 form(保留当前字段值)
      formRef.current.setDraft({
        ...d,
        id: saved.id,
        mailAccountId: saved.mail_account_id ?? d.mailAccountId,
      });
      formRef.current.markSaved();
      setSaveStatus("saved");
      // 真实 upsert 成功 → 通知父级刷新(只有此路径调 onSaved;空 draft 跳过和失败都不调)
      onSavedRef.current?.({ draftId: saved.id, isNew });
      return { ok: true, draftId: saved.id, isNew };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setSaveStatus("error");
      return { ok: false, error: msg };
    } finally {
      setSaving(false);
    }
  }, []);

  // ---- 1c. 上传文件(必须先有 draftId;无 draftId 时先 performSave 拿到 id)----
  const handleAttachFiles = useCallback(
    async (files: FileList | null): Promise<void> => {
      if (!files || files.length === 0) return;
      if (!COMPOSER_REAL) return;
      if (!formRef.current.draft.mailAccountId) {
        onError("メールアカウントが選択されていません");
        return;
      }
      let effectiveDraftId = formRef.current.draft.id;
      // 没有 draftId 时,先 upsert 一次拿到 id(useAttachments 上传需要)
      if (!effectiveDraftId) {
        const result = await performSave();
        if (!result.ok || !result.draftId) {
          onError(`下書きの保存に失敗しました: ${result.error ?? ""}`);
          return;
        }
        effectiveDraftId = result.draftId;
      }
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (!f) continue;
        try {
          await attachments.upload(f, effectiveDraftId);
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          onError(`アップロード失敗: ${msg}`);
          break;
        }
      }
    },
    [attachments, onError, performSave],
  );

  const handleRemoveAttachment = useCallback(
    async (attachmentId: string): Promise<void> => {
      if (!COMPOSER_REAL) return;
      try {
        await attachments.remove(attachmentId);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        onError(`添付削除失敗: ${msg}`);
      }
    },
    [attachments, onError],
  );

  // ---- 3. open 切换 + existingDraftId 变化 → 触发 load / reset ----
  // 关键:每个 session 第一次 mount(open 变 true)时,根据 existingDraftId 决定:
  //   - null → resetToEmpty(mailAccountId)(模式 = "new")
  //   - string → GET /drafts/{id},等待响应后 setDraft(模式 = "existing")
  useEffect(() => {
    if (!open) {
      return; // 关掉时不做事;React unmount 时自然销毁
    }
    if (!mailAccountId) return;

    if (existingDraftId) {
      // 切到 existing draft
      const seq = ++requestSeqRef.current;
      setMode("existing");
      setLoading(true);
      setError(null);
      setSaveStatus("loading");
      mailV2Api
        .getDraft(existingDraftId)
        .then((dto) => {
          if (seq !== requestSeqRef.current) return; // 已被新请求覆盖
          form.setDraft({
            id: dto.id,
            parentMessageId: dto.parent_message_id ?? null,
            mailAccountId: dto.mail_account_id ?? mailAccountId,
            to: dto.to_addresses ?? "",
            cc: dto.cc_addresses ?? "",
            bcc: "",
            subject: dto.subject ?? "",
            bodyText: dto.body_text ?? "",
            bodyHtml: dto.body_html ?? "",
          });
          setSaveStatus("idle");
        })
        .catch((e: unknown) => {
          if (seq !== requestSeqRef.current) return;
          const msg = e instanceof Error ? e.message : String(e);
          setError(msg);
          setSaveStatus("error");
          onError(`下書きの読込に失敗しました: ${msg}`);
        })
        .finally(() => {
          if (seq !== requestSeqRef.current) return;
          setLoading(false);
        });
    } else {
      // 新建邮件 / Reply / Reply-All / Forward — 直接用初期値
      const seq = ++requestSeqRef.current;
      setMode("new");
      const empty = createEmptyDraftForm(mailAccountId);
      form.setDraft({
        ...empty,
        ...initialDraft,
        id: null,
        mailAccountId: initialDraft?.mailAccountId ?? mailAccountId,
        parentMessageId: initialDraft?.parentMessageId ?? parentMessageId,
      });
      setError(null);
      setSaveStatus("idle");
      // 阻止 stale response(虽然这里没有 in-flight 请求,但保持 seq 干净)
      void seq;
    }
    // 注意:form 对象每次 render 都不同,但其 setDraft / resetToEmpty 用 useCallback 稳定
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingDraftId, mailAccountId, parentMessageId, initialDraft, sessionKey]);

  // ---- 5. dirty 派生到 saveStatus("dirty" badge)----
  useEffect(() => {
    if (loading) return;
    if (saving || sending || deleting) return;
    if (form.isDirty) {
      setSaveStatus("dirty");
    } else if (saveStatus === "dirty" || saveStatus === "saved") {
      setSaveStatus("idle");
    }
  }, [form.isDirty, loading, saving, sending, deleting, saveStatus]);

  // ---- 6. close:dirty 时 await save,not dirty 时直接关 ----
  const isDirtyRef = useRef(form.isDirty);
  isDirtyRef.current = form.isDirty;
  const handleUserClose = useCallback(async (): Promise<void> => {
    if (saving || sending || deleting) return;
    if (isDirtyRef.current) {
      const result = await performSave();
      if (!result.ok) {
        onError(`保存に失敗しました: ${result.error ?? ""}`);
        return; // 保留 Editor,让用户决定
      }
    }
    onCloseRef.current();
  }, [saving, sending, deleting, performSave, onError]);

  // ---- 6b. 暴露 imperative close handle 给父组件 ----
  // 父组件在切 draft / 切 folder 前 await 此 handle:dirty 时 await save。
  // 防重入:saving/sending/deleting 中再次调用直接 resolve(空)。
  useEffect(() => {
    if (!requestCloseHandle) return;
    requestCloseHandle.current = async () => {
      if (saving || sending || deleting) return;
      if (!isDirtyRef.current) return;
      // 复用 performSave 逻辑但不 onCloseRef()(只 save)
      const result = await performSave();
      if (!result.ok) {
        onError(`保存に失敗しました: ${result.error ?? ""}`);
      }
    };
    return () => {
      requestCloseHandle.current = null;
    };
  }, [requestCloseHandle, saving, sending, deleting, performSave, onError]);

  // ---- 6c. 暴露 imperative insert handle 给父组件 ----
  // 2026-07-24: AI 生成结果(text)由父组件 MailV2Page 触发,append 到 bodyText,
  // bodyHtml 同步通过 plainTextToHtml()。
  // 依赖 form (每次 render form.draft.bodyText 拿最新值)。
  useEffect(() => {
    if (!insertBodyHandle) return;
    insertBodyHandle.current = (text: string) => {
      const existing = form.draft.bodyText || "";
      const next = existing ? `${existing}\n\n${text}` : text;
      form.setField("bodyText", next);
      form.setField("bodyHtml", plainTextToHtml(next));
    };
    return () => {
      insertBodyHandle.current = null;
    };
  }, [insertBodyHandle, form]);

  // ---- 7. send:save → send → onSent → close ----
  const handleSend = useCallback(async (): Promise<void> => {
    if (saving || sending || deleting) return;
    setSending(true);
    setError(null);
    try {
      // 先保证 DB 里有最新内容(可能有 draftId 也可能没)
      const saveResult = await performSave();
      if (!saveResult.ok || !saveResult.draftId) {
        onError(`保存に失敗しました: ${saveResult.error ?? ""}`);
        return;
      }
      const draftId = saveResult.draftId;
      // 发信
      const sent = await mailV2Api.sendDraft(draftId, newIdempotencyKey());
      onSent({ id: sent.id, subject: sent.subject ?? null });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      onError(`送信に失敗しました: ${msg}`);
    } finally {
      setSending(false);
    }
  }, [saving, sending, deleting, performSave, onSent, onError]);

  // ---- 8. delete:DELETE /drafts/{id} → onDeleted → close ----
  // 注意:draftId 必须存在(只有 existing 模式能删);new 模式不调
  const handleDelete = useCallback(async (): Promise<void> => {
    const d = formRef.current.draft;
    if (!d.id) {
      // new 模式:没有 db row,直接关
      onCloseRef.current();
      return;
    }
    if (!UUIDV4_RE.test(d.id)) {
      onError("無効な draft id");
      return;
    }
    if (deleting) return;
    if (!window.confirm("この下書きを削除しますか?この操作は取り消せません。")) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await mailV2Api.deleteDraft(d.id);
      if (onDeleted) onDeleted(d.id);
      onCloseRef.current();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      onError(`削除に失敗しました: ${msg}`);
    } finally {
      setDeleting(false);
    }
  }, [deleting, onDeleted, onError]);

  // ---- 10. Escape 监听 ----
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      void handleUserClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleUserClose]);

  // ---- 11. 派生:canSend / sending ----
  const canSend =
    !!form.draft.to.trim() &&
    !!form.draft.subject.trim() &&
    !saving &&
    !sending &&
    !deleting;
  const isSending = sending || (form.isDirty === false && saveStatus === "saving");

  // ---- 12. 渲染:loading 时整片显示「読込中…」(不显示任何旧字段)----
  if (loading || (!form.draft.id && mode === "existing")) {
    return (
      <div
        className="mv2-compose-backdrop"
        data-testid="mv2-compose-backdrop"
      >
        <div
          className={`mv2-compose-dialog${contextPane ? " has-context" : ""}`}
          role="dialog"
          aria-label="メール"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mv2-compose-header">
            <div className="mv2-compose-title">
              {saveStatus === "error" ? "読込に失敗しました" : "読込中…"}
            </div>
            <button
              type="button"
              className="mv2-compose-close"
              onClick={() => void handleUserClose()}
              aria-label="閉じる"
              data-testid="mv2-compose-close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mv2-compose-loading" data-testid="mv2-compose-loading">
            {error ?? "下書きを読込中…"}
          </div>
        </div>
      </div>
    );
  }

  // ---- 13. 渲染:正常 Editor + Dialog shell ----
  return (
    <div
      className="mv2-compose-backdrop"
      data-testid="mv2-compose-backdrop"
      data-saving={saving || sending || deleting || undefined}
    >
      <div
        className={`mv2-compose-dialog${contextPane ? " has-context" : ""}`}
        role="dialog"
        aria-label={mode === "new" ? "新しいメール" : "下書き編集"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mv2-compose-header">
          <div className="mv2-compose-title">
            {saving
              ? "保存中…"
              : sending
                ? "送信中…"
                : deleting
                  ? "削除中…"
                  : saveStatus === "error"
                    ? "エラー"
                    : mode === "new"
                      ? "新しいメール"
                      : "下書き編集"}
          </div>
          <button
            type="button"
            className="mv2-compose-close"
            onClick={() => void handleUserClose()}
            disabled={saving || sending || deleting}
            aria-label="閉じる"
            data-testid="mv2-compose-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* 错误条 */}
        {(saveStatus === "error" || error) && (
          <div className="mv2-compose-error" role="alert" data-testid="mv2-compose-save-error">
            <AlertCircle size={14} />
            <span style={{ flex: 1 }}>
              {error ?? "保存に失敗しました"}
            </span>
          </div>
        )}

        {/* 2026-07-24: 主区域 + AI Drawer slot 横向 flex(dialog 已加宽)。 */}
        <div className={`mv2-compose-body${contextPane ? " has-context" : ""}`}>
          {contextPane ? (
            <div className="mv2-compose-context-slot" data-testid="mv2-compose-context-slot">
              {contextPane}
            </div>
          ) : null}
          <div className="mv2-compose-main">
            <MailEditor
              values={form.draft}
              onChange={form.setField}
              mode={mode === "existing" ? "edit-existing" : "new"}
              saveStatus={saveStatus}
              sending={isSending}
              canSend={canSend}
              onSend={() => void handleSend()}
              onDeleteDraft={mode === "existing" ? () => void handleDelete() : undefined}
              hasDraft={!!form.draft.id}
              deleting={deleting}
              attachments={attachments.items}
              uploadingCount={attachments.uploading}
              onAttachFiles={handleAttachFiles}
              onRemoveAttachment={handleRemoveAttachment}
              onAiClick={onOpenAiDrawer}
              editorKey={sessionKey}
              data-testid="mv2-compose-editor"
            />
          </div>
          {isAiDrawerOpen && aiDrawer && (
            <div className="mv2-compose-ai-slot" data-testid="mv2-compose-ai-slot">
              {aiDrawer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
